import { StockWithVariant } from '@/api/daily-stock-api';
import { StockTable, StockTableMobile } from '@/components/stock';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search } from 'lucide-react';
import { useEffect, useRef } from 'react';

interface StockListProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  activeTab: string;
  onTabChange: (value: string) => void;
  stocks: StockWithVariant[];
  filteredStocks: StockWithVariant[];
  stats: {
    lowStockItems: number;
    outOfStockItems: number;
    totalItems: number;
  };
  totalElements: number;
  isLoading: boolean;
  isMobile: boolean;
  onStockUpdate?: () => void;
  onLoadMore?: () => void;
  isLastPage?: boolean;
  isValidating?: boolean;
}

export function StockList({
  searchTerm,
  onSearchChange,
  activeTab,
  onTabChange,
  stocks,
  filteredStocks,
  stats,
  totalElements,
  isLoading,
  isMobile,
  onStockUpdate,
  onLoadMore,
  isLastPage,
  isValidating,
}: StockListProps) {
  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!loadMoreRef.current || isLastPage || isLoading || isValidating) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          onLoadMore?.();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    observer.observe(loadMoreRef.current);

    return () => {
      observer.disconnect();
    };
  }, [onLoadMore, isLastPage, isLoading, isValidating]);

  return (
    <Card className="mt-4 md:mt-5 w-full max-w-full overflow-x-hidden">
      <CardHeader className="px-4 pt-4 md:px-6 md:pt-6">
        <div className="flex flex-col sm:flex-row gap-3 md:gap-4 items-start sm:items-center justify-between w-full max-w-full">
          <div className="w-full sm:w-auto min-w-0">
            <CardTitle className="text-base md:text-lg">Stock Inventory</CardTitle>
            <CardDescription className="text-xs md:text-sm mt-1">
              Showing {filteredStocks.length} of {totalElements} stock items
            </CardDescription>
          </div>
          <div className="flex items-center space-x-2 w-full sm:w-auto min-w-0">
            <Search className="h-4 w-4 text-muted-foreground shrink-0" />
            <Input
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full sm:max-w-sm min-w-0"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-4 md:px-6 md:pb-6 w-full max-w-full overflow-x-hidden">
        <Tabs value={activeTab} onValueChange={onTabChange}>
          <TabsList className="grid w-full grid-cols-3 lg:w-auto h-auto">
            <TabsTrigger
              value="out-of-stock"
              className="flex items-center gap-1.5 md:gap-2 text-xs md:text-sm py-2 md:py-2.5 px-2 md:px-3"
            >
              <span className="hidden sm:inline">Out of Stock</span>
              <span className="sm:hidden">Out</span>
            </TabsTrigger>
            <TabsTrigger
              value="low-stock"
              className="flex items-center gap-1.5 md:gap-2 text-xs md:text-sm py-2 md:py-2.5 px-2 md:px-3"
            >
              Low Stock
            </TabsTrigger>
            <TabsTrigger
              value="today"
              className="flex items-center gap-1.5 md:gap-2 text-xs md:text-sm py-2 md:py-2.5 px-2 md:px-3"
            >
              <span className="hidden sm:inline">Today's Stock</span>
              <span className="sm:hidden">Today</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-3 md:mt-4">
            {isMobile ? (
              <StockTableMobile
                stocks={filteredStocks}
                isLoading={isLoading}
                onStockUpdate={onStockUpdate}
              />
            ) : (
              <div className="space-y-2 w-full max-w-full overflow-x-hidden">
                <StockTable
                  stocks={filteredStocks}
                  isLoading={isLoading}
                  onStockUpdate={onStockUpdate}
                />
              </div>
            )}

            {/* Scroll Anchor */}
            <div ref={loadMoreRef} className="h-10 w-full flex items-center justify-center mt-2">
              {isValidating && !isLoading && (
                <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-primary"></div>
                  <span>Loading more items...</span>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
